/**
 * API Client for ePeople Agent Platform
 * Handles all API requests with authentication, retries, and error handling
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// API Configuration
const API_BASE_URL = (import.meta.env?.VITE_API_BASE_URL as string) || 'https://api-dev.epeople.work';
const API_TIMEOUT = 30000; // 30 seconds

// Token storage keys
const ACCESS_TOKEN_KEY = 'epeople_access_token';
const REFRESH_TOKEN_KEY = 'epeople_refresh_token';
const USER_DATA_KEY = 'epeople_user_data';

/**
 * API Response Types
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  idToken?: string;
}

export interface UserProfile {
  userId: string;
  email: string;
  role: string;
  accountType?: string;
  tenantId?: string;
  orgId?: string;
  firstName?: string;
  lastName?: string;
  emailVerified: boolean;
  onboardingCompleted?: boolean;
}

export interface SignupRequest {
  email: string;
  password: string;
  role: string;
  firstName?: string;
  lastName?: string;
  orgName?: string;
  orgSubdomain?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface VerifyEmailRequest {
  email: string;
  code: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Token Management
 */
class TokenManager {
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setTokens(tokens: AuthTokens): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  }

  getUserData(): UserProfile | null {
    const data = localStorage.getItem(USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  }

  setUserData(user: UserProfile): void {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
  }
}

export const tokenManager = new TokenManager();

/**
 * API Client Class
 */
class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = tokenManager.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - Handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // If error is 401 and we haven't retried yet, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Wait for the refresh to complete
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(this.client(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = tokenManager.getRefreshToken();
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            const response = await this.client.post<ApiResponse<AuthTokens>>(
              '/auth/refresh-token',
              { refreshToken }
            );

            if (response.data.success && response.data.data) {
              const { accessToken } = response.data.data;
              tokenManager.setTokens(response.data.data);

              // Retry all queued requests with new token
              this.refreshSubscribers.forEach((callback) => callback(accessToken));
              this.refreshSubscribers = [];

              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              }
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            tokenManager.clearTokens();
            window.location.href = '/login?session=expired';
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Generic request method with error handling
   */
  private async request<T>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: unknown
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.request<ApiResponse<T>>({
        method,
        url,
        data,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiResponse>;
        return {
          success: false,
          error: axiosError.response?.data?.error || axiosError.message || 'An error occurred',
        };
      }
      return {
        success: false,
        error: 'An unexpected error occurred',
      };
    }
  }

  /**
   * Authentication Methods
   */

  async signup(data: SignupRequest): Promise<ApiResponse<{ userId: string; email: string }>> {
    return this.request('post', '/auth/signup', data);
  }

  async login(data: LoginRequest): Promise<ApiResponse<AuthTokens & { user: UserProfile }>> {
    const response = await this.request<AuthTokens & { user: UserProfile }>('post', '/auth/login', data);
    
    if (response.success && response.data) {
      // Store tokens and user data
      tokenManager.setTokens({
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        expiresIn: response.data.expiresIn,
        idToken: response.data.idToken,
      });
      tokenManager.setUserData(response.data.user);
    }
    
    return response;
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<ApiResponse<{ message: string }>> {
    return this.request('post', '/auth/verify-email', data);
  }

  async refreshToken(data: RefreshTokenRequest): Promise<ApiResponse<AuthTokens>> {
    const response = await this.request<AuthTokens>('post', '/auth/refresh-token', data);
    
    if (response.success && response.data) {
      tokenManager.setTokens(response.data);
    }
    
    return response;
  }

  async getProfile(): Promise<ApiResponse<UserProfile>> {
    const response = await this.request<UserProfile>('get', '/auth/profile');
    
    if (response.success && response.data) {
      tokenManager.setUserData(response.data);
    }
    
    return response;
  }

  /**
   * Logout - Clear tokens
   */
  logout(): void {
    tokenManager.clearTokens();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!tokenManager.getAccessToken();
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): UserProfile | null {
    return tokenManager.getUserData();
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export default for convenience
export default apiClient;
